import { Fragment, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/cn";
import { Card } from "./Card";
import { Row, Stack } from "./Stack";

export interface ColumnaDataList<T> {
  clave: string;
  titulo: string;
  render: (fila: T) => ReactNode;
}

export interface DataListProps<T> {
  etiqueta: string;
  columnas: ColumnaDataList<T>[];
  filas: T[];
  claveFila: (fila: T) => string | number;
  acciones?: (fila: T) => ReactNode;
  vacio?: string;
  /** Desglose de una fila, debajo de ella a todo el ancho (null = cerrada). */
  detalle?: (fila: T) => ReactNode;
  /** Toda la fila (o la tarjeta) se toca para abrirla; también con Enter. Los botones y links de adentro siguen siendo suyos. */
  alElegirFila?: (fila: T) => void;
  /** Clases de una fila (o su tarjeta), ej. el fondo de un estado: `bg-exito-suave`. */
  claseFila?: (fila: T) => string | undefined;
}

/** Un click en un botón, link o campo de la fila es de ese control, no de la fila. */
const esDeUnControl = (e: MouseEvent<HTMLElement>) => (e.target as HTMLElement).closest("button, a, input, select, textarea, [role='combobox']") !== null;

function propsElegible<T>(fila: T, alElegirFila?: (fila: T) => void) {
  if (!alElegirFila) return {};
  return {
    tabIndex: 0,
    onClick: (e: MouseEvent<HTMLElement>) => {
      if (!esDeUnControl(e)) alElegirFila(fila);
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === "Enter" && e.target === e.currentTarget) alElegirFila(fila);
    },
  };
}

/** Listado: tabla en la compu, tarjetas en el celular. */
export function DataList<T>({ etiqueta, columnas, filas, claveFila, acciones, vacio = "No hay datos para mostrar.", detalle, alElegirFila, claseFila }: DataListProps<T>) {
  const esMobile = useIsMobile();

  if (filas.length === 0) {
    return (
      <Card>
        <p className="text-texto-suave">{vacio}</p>
      </Card>
    );
  }

  if (esMobile) {
    return (
      <ul aria-label={etiqueta} className="flex flex-col gap-3">
        {filas.map((fila) => (
          <li key={claveFila(fila)} {...propsElegible(fila, alElegirFila)} className={alElegirFila ? "cursor-pointer rounded-lg focus-visible:outline-2 focus-visible:outline-primario" : undefined}>
            <Card className={claseFila?.(fila)}>
              <Stack espacio={3}>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  {columnas.map((c) => (
                    <Fragment key={c.clave}>
                      <dt className="text-[0.6875rem] uppercase tracking-[0.14em] text-texto-suave">{c.titulo}</dt>
                      <dd className="min-w-0 break-words">{c.render(fila)}</dd>
                    </Fragment>
                  ))}
                </dl>
                {acciones && (
                  <Row envolver espacio={2}>
                    {acciones(fila)}
                  </Row>
                )}
                {detalle?.(fila)}
              </Stack>
            </Card>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Card relleno="ninguno" className="overflow-x-auto">
      <table aria-label={etiqueta} className="w-full text-left">
        <thead className="border-b border-borde text-[0.6875rem] uppercase tracking-[0.14em] text-texto-suave">
          <tr>
            {columnas.map((c) => (
              <th key={c.clave} scope="col" className="px-4 py-3 font-medium">
                {c.titulo}
              </th>
            ))}
            {acciones && (
              <th scope="col" className="px-4 py-3">
                <span className="sr-only">Acciones</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila) => {
            const desglose = detalle?.(fila);
            return (
              <Fragment key={claveFila(fila)}>
                <tr
                  {...propsElegible(fila, alElegirFila)}
                  className={cn("border-b border-borde last:border-0", claseFila?.(fila), alElegirFila && "cursor-pointer hover:bg-superficie-2 focus-visible:outline-2 focus-visible:outline-primario")}
                >
                  {columnas.map((c) => (
                    <td key={c.clave} className="px-4 py-3 align-middle">
                      {c.render(fila)}
                    </td>
                  ))}
                  {acciones && (
                    <td className="px-4 py-3">
                      <Row justificar="fin" espacio={2}>
                        {acciones(fila)}
                      </Row>
                    </td>
                  )}
                </tr>
                {desglose != null && (
                  <tr className="border-b border-borde bg-superficie-2 last:border-0">
                    <td colSpan={columnas.length + (acciones ? 1 : 0)} className="px-4 py-3">
                      {desglose}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
